import { getRandomInt } from 'utils';
import { ShipLocation as Ship, Coordinates, Orientation, Team } from './types';

export class ShipGenerationError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'ValidationError';
    }
}

export class BattleshipGenerator {
    constructor(
        private readonly gridColumns: number,
        private readonly gridRows: number,
        public disableGeneration: boolean = false,
        public ships: Ship[] = [],
        private readonly maxAttempts: number = 150
    ) {}

    public static getShipListCoordinates(ships: Ship[]): Coordinates[] {
        return ships.map((ship) => BattleshipGenerator.getShipCoordinates(ship)).flat();
    }

    public static getShipCoordinates(ship: Ship): Coordinates[] {
        const points: Coordinates[] = [{ ...ship.startCoordinate }];
        if (ship.orientation === Orientation.HORIZONTAL) {
            for (let i = 1; i < ship.size; i++) {
                points.push({ x: ship.startCoordinate.x + i, y: ship.startCoordinate.y });
            }
            return points;
        }
        // Orientation.VERTICAL
        for (let i = 1; i < ship.size; i++) {
            points.push({ x: ship.startCoordinate.x, y: ship.startCoordinate.y + i });
        }

        return points;
    }

    public static getShipName(shipSize: number) {
        switch (shipSize) {
            case 1:
                return 'Submarine';
            case 2:
                return 'Destroyer';
            case 3:
                return 'Cruiser';
            case 5:
                return 'Aircraft Carrier';
            case 4:
            default:
                return 'Battleship';
        }
    }

    /**
     * Generates ship locations for the given ship sizes array.
     * @param shipSizesArray array defining ship counts where the index in the array defines the ship size as index+1
     * @returns An array of ship definitions
     */
    public generateRandomShipLocations(shipSizesArray: number[]): Ship[] {
        if (this.disableGeneration) {
            return [];
        }
        // TODO - figure out a way to avoid failure of ship generations
        return [...shipSizesArray]
            .reverse()
            .map((shipCount, index) => {
                const newShips: Ship[] = [];
                for (let i = 0; i < shipCount; i++) {
                    newShips.push(this.generateRandomShipLocation(shipSizesArray.length - index));
                }
                return newShips;
            })
            .flat();
    }

    /**
     * Generates a ship of the defined size.
     * If system cannot generate a valid ship in the maxAttempts defined on the BattleshipGenerator instance an exception will be thrown.
     * @param size The ship size
     * @returns Ship definition
     */
    private generateRandomShipLocation(size: number): Ship {
        let point: Coordinates | undefined;
        let orientation: Orientation | undefined;
        let attempts = 0;

        while (
            (!point || !orientation || this.isPointOccupied(point) || !this.isShipPlacementPossible(size, point, orientation)) &&
            attempts < this.maxAttempts
        ) {
            attempts++;
            orientation = getRandomInt(0, 1) === 0 ? Orientation.HORIZONTAL : Orientation.VERTICAL;
            point = this.getRandomStartingPoint(size, orientation);
        }

        // TODO - figure out a way to avoid failure of ship generations
        if (attempts >= this.maxAttempts) {
            console.warn(`Could not generate ship location after ${this.maxAttempts} attempts.`);
            throw new ShipGenerationError(`Could not generate ship location after ${this.maxAttempts} attempts.`);
        }

        const ship = {
            startCoordinate: point!,
            orientation: orientation!,
            size
        };
        this.ships.push(ship);
        return ship;
    }

    private getRandomStartingPoint(size: number, orientation: Orientation): Coordinates {
        let point: Coordinates;
        //do {
        point =
            orientation === Orientation.HORIZONTAL
                ? this.getRandomPoint(this.gridColumns - size + 1, this.gridRows)
                : this.getRandomPoint(this.gridColumns, this.gridRows - size + 1);
        //} while (this.isPointOccupied(point) || !this.isShipPlacementPossible(size, point, orientation));

        return point;
    }

    private isShipPlacementPossible(shipSize: number, point: Coordinates, orientation: Orientation): boolean {
        // Generate temporary coordinates to check for potential overlap
        const tempShip = { startCoordinate: point, orientation, size: shipSize };
        const shipCoordinates = BattleshipGenerator.getShipCoordinates(tempShip);

        // Check each point for overlap
        return shipCoordinates.every((coord) => !this.isPointOccupied(coord) && this.isWithinBounds(coord));
    }

    private isWithinBounds(point: Coordinates): boolean {
        return point.x < this.gridColumns && point.y < this.gridRows;
    }

    private getRandomPoint(columns: number, rows: number): Coordinates {
        const x = getRandomInt(0, columns);
        const y = getRandomInt(0, rows);
        return { x, y };
    }

    private getValidOrientation(shipSize: number, startingPoint: Coordinates): Orientation | undefined {
        const possibleOrientations: Orientation[] = [];
        if (startingPoint.x + shipSize <= this.gridColumns) {
            possibleOrientations.push(Orientation.HORIZONTAL);
        }
        if (startingPoint.y + shipSize <= this.gridRows) {
            possibleOrientations.push(Orientation.VERTICAL);
        }

        if (possibleOrientations.length === 0) {
            return undefined;
        }

        return possibleOrientations.length === 1 ? possibleOrientations[0] : possibleOrientations[getRandomInt(0, 1)];
    }

    private isPointOccupied(point: Coordinates): boolean {
        return this.ships.some((ship) =>
            BattleshipGenerator.getShipCoordinates(ship).some((occupiedPoint) => occupiedPoint.x === point.x && occupiedPoint.y === point.y)
        );
    }

    // private isShipValid(shipSize: number, point: Coordinates, orientation: Orientation): boolean {
    //     return BattleshipGenerator.getShipCoordinates({ size: shipSize, startCoordinate: point, orientation }).every(
    //         (point) => !this.isPointOccupied(point) && point.x < this.gridColumns && point.y < this.gridRows
    //     );
    // }

    /**
     * Clears all existing ships from the generator.
     */
    public clearShips(): void {
        this.ships = [];
    }

    /**
     * Removes the specified ships from the current list of ships.
     * @param shipsToRemove Array of ships to remove.
     */
    public removeShips(shipsToRemove: Ship[]): void {
        this.ships = this.ships.filter((ship) => !shipsToRemove.includes(ship));
    }

    /**
     * Generates ships for each provided team object.
     * @param teams The array of team objects with only the name property filled in
     * @param shipSizesArray The array of ship sizes to generate for each team
     * @returns The same team array with the shipLocations filled in
     */
    public generateShipsForTeams(teams: Team[], shipSizesArray: number[]): Team[] {
        teams.forEach((team) => {
            // Clear previous shipLocations for the current team
            team.shipLocations = [];
            // Assign a temporary ships array to the generator for generation
            this.ships = team.shipLocations;
            // Generate ship locations based on the sizes provided
            const generatedShips = this.generateRandomShipLocations(shipSizesArray);
            // After generation, assign the generated ships to the team's shipLocations
            team.shipLocations = generatedShips;
        });

        return teams;
    }
}
